/**
 * Supplier List Component
 *
 * Displays suppliers with filtering, sorting, and management actions
 *
 * @module pages/suppliers/SupplierList
 */

import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Badge,
  Form,
  InputGroup,
  Dropdown,
  Spinner,
  Alert,
  Modal,
} from 'react-bootstrap';
import { DataTable } from '../../components/common/DataTable';
import KebabMenu from '../../components/common/KebabMenu';
import {
  listSuppliers,
  deleteSupplier,
  toggleSupplierStatus,
  getSupplierSummary,
} from '../../services/supplierService';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from '../../utils/toast';
import { unwrapServiceResult } from '../../utils/serviceResult';
import useViewToggle from '../../hooks/useViewToggle';
import ViewToggle from '../../components/common/ViewToggle';
import DashboardLayout from '../../components/layout/DashboardLayout';
import './SupplierList.css';

// Payment terms display mapping
const PAYMENT_TERMS_LABELS = {
  'COD': 'Contra Entrega',
  'Net 15': 'Neto 15 dias',
  'Net 30': 'Neto 30 dias',
  'Net 60': 'Neto 60 dias',
};

const SupplierList = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isAdmin, isTechnician } = useAuth();
  const { viewMode, setViewMode, isTableView } = useViewToggle();

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all, active, inactive
  const [sortBy, setSortBy] = useState('name');
  const [deleteModalShow, setDeleteModalShow] = useState(false);
  const [supplierToDelete, setSupplierToDelete] = useState(null);

  const canEdit = isAdmin || isTechnician;

  // Fetch suppliers
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['suppliers', { search: searchTerm, status: statusFilter }],
    // Sin `unwrapServiceResult` un fallo de red/RLS resolvía con
    // `{data: null, error}` y la pantalla mostraba el estado vacío ("no hay
    // proveedores") en vez del bloque de error con Reintentar.
    queryFn: () =>
      unwrapServiceResult(listSuppliers({
        search: searchTerm,
        isActive: statusFilter === 'all' ? undefined : statusFilter === 'active',
      })),
  });

  // Fetch supplier summary
  const { data: summaryData } = useQuery({
    queryKey: ['suppliers-summary'],
    queryFn: getSupplierSummary,
  });

  // Delete mutation
  //
  // `deleteSupplier`/`toggleSupplierStatus` DEVUELVEN el error en vez de
  // lanzarlo, así que la promesa se resuelve igual cuando falla y react-query
  // corría `onSuccess`: un rechazo de RLS o un FK violado terminaba en el
  // toast verde "Proveedor eliminado exitosamente" con el proveedor intacto
  // en pantalla. `onError` era código muerto. `unwrapServiceResult` convierte
  // el error devuelto en un error lanzado (ver utils/serviceResult.js).
  const deleteMutation = useMutation({
    mutationFn: (id) => unwrapServiceResult(deleteSupplier(id)),
    onSuccess: (result) => {
      if (result.softDeleted) {
        toast.success('Proveedor desactivado (tiene compras asociadas)');
      } else {
        toast.success('Proveedor eliminado exitosamente');
      }
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
      queryClient.invalidateQueries({ queryKey: ['suppliers-summary'] });
      setDeleteModalShow(false);
      setSupplierToDelete(null);
    },
    onError: (error) => {
      toast.error(`Error al eliminar: ${error.message}`);
    },
  });

  // Toggle status mutation
  const toggleStatusMutation = useMutation({
    mutationFn: ({ id, isActive }) => unwrapServiceResult(toggleSupplierStatus(id, isActive)),
    onSuccess: (result, { isActive }) => {
      toast.success(isActive ? 'Proveedor activado' : 'Proveedor desactivado');
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
      queryClient.invalidateQueries({ queryKey: ['suppliers-summary'] });
    },
    onError: (error) => {
      toast.error(`Error al cambiar estado: ${error.message}`);
    },
  });

  const suppliers = data?.data || [];
  const summary = summaryData?.data || {};

  // Sort suppliers
  const sortedSuppliers = [...suppliers].sort((a, b) => {
    switch (sortBy) {
      case 'name':
        return a.name.localeCompare(b.name);
      case 'contact':
        return (a.contact_name || '').localeCompare(b.contact_name || '');
      case 'created':
        return new Date(b.created_at) - new Date(a.created_at);
      default:
        return 0;
    }
  });

  // Get status badge
  const getStatusBadge = (isActive) => {
    return isActive ? (
      <Badge bg="success">Activo</Badge>
    ) : (
      <Badge bg="secondary">Inactivo</Badge>
    );
  };

  const handleDeleteClick = (supplier) => {
    setSupplierToDelete(supplier);
    setDeleteModalShow(true);
  };

  const handleConfirmDelete = () => {
    if (supplierToDelete) {
      deleteMutation.mutate(supplierToDelete.id);
    }
  };

  const handleToggleStatus = (supplier) => {
    toggleStatusMutation.mutate({
      id: supplier.id,
      isActive: !supplier.is_active,
    });
  };

  const supplierColumns = useMemo(() => [
    {
      id: 'name',
      header: 'Nombre',
      accessor: 'name',
      minWidth: 180,
      cell: (s) => (
        <div>
          <strong>{s.name}</strong>
          {s.tax_id && <small className="d-block text-muted">ID: {s.tax_id}</small>}
        </div>
      ),
    },
    {
      id: 'contact',
      header: 'Contacto',
      accessor: (s) => s.contact_name || '',
      minWidth: 140,
      cell: (s) => s.contact_name || '-',
    },
    {
      id: 'phone',
      header: 'Telefono',
      accessor: (s) => s.phone || '',
      minWidth: 120,
      cell: (s) => s.phone ? (
        <a href={`tel:${s.phone}`} className="text-decoration-none" onClick={(e) => e.stopPropagation()}>
          {s.phone}
        </a>
      ) : '-',
    },
    {
      id: 'email',
      header: 'Email',
      accessor: (s) => s.email || '',
      minWidth: 180,
      cell: (s) => s.email ? (
        <a href={`mailto:${s.email}`} className="text-decoration-none" onClick={(e) => e.stopPropagation()}>
          {s.email}
        </a>
      ) : '-',
    },
    {
      id: 'payment_terms',
      header: 'Terminos de Pago',
      accessor: (s) => s.payment_terms || '',
      minWidth: 140,
      cell: (s) => s.payment_terms ? (PAYMENT_TERMS_LABELS[s.payment_terms] || s.payment_terms) : '-',
    },
    {
      id: 'status',
      header: 'Estado',
      accessor: (s) => (s.is_active ? 1 : 0),
      minWidth: 100,
      cell: (s) => getStatusBadge(s.is_active),
    },
    {
      id: 'actions',
      header: '',
      sortable: false,
      resizable: false,
      align: 'center',
      noSelect: true,
      minWidth: 50,
      maxWidth: 60,
      cell: (supplier) => (
        <div data-no-select>
          <KebabMenu
            label={`Acciones de ${supplier.name}`}
            dropdownProps={{ popperConfig: { strategy: 'fixed' } }}
          >
              {canEdit && (
                <>
                  <Dropdown.Item onClick={() => navigate(`/proveedores/${supplier.id}/editar`)}>
                    <i className="bi bi-pencil me-2"></i>Editar
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => handleToggleStatus(supplier)}>
                    <i className={`bi ${supplier.is_active ? 'bi-pause-circle' : 'bi-play-circle'} me-2`}></i>
                    {supplier.is_active ? 'Desactivar' : 'Activar'}
                  </Dropdown.Item>
                  <Dropdown.Divider />
                  <Dropdown.Item className="text-danger" onClick={() => handleDeleteClick(supplier)}>
                    <i className="bi bi-trash me-2"></i>Eliminar
                  </Dropdown.Item>
                </>
              )}
          </KebabMenu>
        </div>
      ),
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [navigate, canEdit]);

  if (isLoading) {
    return (
      <DashboardLayout>
        <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Cargando proveedores...</span>
          </Spinner>
        </Container>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <Container className="mt-4">
          <Alert variant="danger">
            <Alert.Heading>Error al cargar proveedores</Alert.Heading>
            <p>{error.message}</p>
            <Button variant="outline-danger" onClick={() => refetch()}>
              Reintentar
            </Button>
          </Alert>
        </Container>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Container fluid className="py-4 supplier-list">
        {/* Header */}
        <Row className="mb-4">
          <Col>
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
              <div>
                <h1 className="h3 mb-0">Proveedores</h1>
                <p className="text-muted mb-0">{suppliers.length} proveedor{suppliers.length !== 1 ? 'es' : ''}</p>
              </div>
              {canEdit && (
                <Button
                  variant="primary"
                  onClick={() => navigate('/proveedores/nuevo')}
                >
                  <i className="bi bi-plus-circle me-2"></i>
                  <span className="d-none d-sm-inline">Nuevo Proveedor</span>
                  <span className="d-inline d-sm-none">Nuevo</span>
                </Button>
              )}
            </div>
          </Col>
        </Row>

        {/* Summary Statistics */}
        {summary.totalSuppliers > 0 && (
          <Row className="mb-4 g-3">
            <Col xs={12} sm={4}>
              <Card className="h-100 border-0 shadow-sm">
                <Card.Body>
                  <div className="d-flex align-items-center">
                    <div className="flex-shrink-0">
                      <div className="bg-primary bg-opacity-10 rounded p-3">
                        <i className="bi bi-building fs-4 text-primary"></i>
                      </div>
                    </div>
                    <div className="flex-grow-1 ms-3">
                      <p className="text-muted mb-0 small">Total Proveedores</p>
                      <h3 className="mb-0">{summary.totalSuppliers}</h3>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col xs={12} sm={4}>
              <Card className="h-100 border-0 shadow-sm">
                <Card.Body>
                  <div className="d-flex align-items-center">
                    <div className="flex-shrink-0">
                      <div className="bg-success bg-opacity-10 rounded p-3">
                        <i className="bi bi-check-circle fs-4 text-success"></i>
                      </div>
                    </div>
                    <div className="flex-grow-1 ms-3">
                      <p className="text-muted mb-0 small">Activos</p>
                      <h3 className="mb-0">{summary.activeSuppliers}</h3>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col xs={12} sm={4}>
              <Card className="h-100 border-0 shadow-sm">
                <Card.Body>
                  <div className="d-flex align-items-center">
                    <div className="flex-shrink-0">
                      <div className="bg-secondary bg-opacity-10 rounded p-3">
                        <i className="bi bi-pause-circle fs-4 text-secondary"></i>
                      </div>
                    </div>
                    <div className="flex-grow-1 ms-3">
                      <p className="text-muted mb-0 small">Inactivos</p>
                      <h3 className="mb-0">{summary.inactiveSuppliers}</h3>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {/* Filters */}
        <Row className="mb-4 g-3 align-items-center">
          <Col xs={12} md={5}>
            <InputGroup>
              <InputGroup.Text>
                <i className="bi bi-search"></i>
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Buscar por nombre, contacto o email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <Button
                  variant="outline-secondary"
                  onClick={() => setSearchTerm('')}
                >
                  <i className="bi bi-x"></i>
                </Button>
              )}
            </InputGroup>
          </Col>
          <Col xs={5} md={3}>
            <Form.Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">Todos los estados</option>
              <option value="active">Solo activos</option>
              <option value="inactive">Solo inactivos</option>
            </Form.Select>
          </Col>
          <Col xs={5} md={3}>
            <Dropdown onSelect={(key) => setSortBy(key)}>
              <Dropdown.Toggle variant="outline-secondary" className="w-100">
                <i className="bi bi-sort-down me-2"></i>
                <span className="d-none d-sm-inline">Ordenar</span>
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item eventKey="name" active={sortBy === 'name'}>
                  Nombre (A-Z)
                </Dropdown.Item>
                <Dropdown.Item eventKey="contact" active={sortBy === 'contact'}>
                  Contacto (A-Z)
                </Dropdown.Item>
                <Dropdown.Item eventKey="created" active={sortBy === 'created'}>
                  Mas recientes
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </Col>
          <Col xs={2} md={1} className="d-flex justify-content-end">
            <ViewToggle viewMode={viewMode} onToggle={setViewMode} />
          </Col>
        </Row>

        {/* Supplier Content */}
        {sortedSuppliers.length > 0 ? (
          isTableView ? (
          <Row>
          <Col>
            <Card className="shadow-sm">
              <Card.Body className="p-0">
                <DataTable
                  tableId="suppliers"
                  data={sortedSuppliers}
                  rowKey={(s) => s.id}
                  columns={supplierColumns}
                />
              </Card.Body>
            </Card>
          </Col>
        </Row>
          ) : (
          <Row className="g-3">
            {sortedSuppliers.map((supplier) => (
              <Col xs={12} sm={6} lg={4} key={supplier.id}>
                <Card className="shadow-sm supplier-card">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-start mb-3">
                      <div className="flex-grow-1">
                        <div className="d-flex align-items-center gap-2 mb-1">
                          <div className="supplier-icon">
                            <i className="bi bi-building"></i>
                          </div>
                          <h6 className="mb-0">{supplier.name}</h6>
                        </div>
                        {supplier.tax_id && (
                          <small className="text-muted">ID: {supplier.tax_id}</small>
                        )}
                      </div>
                      <div>
                        {getStatusBadge(supplier.is_active)}
                      </div>
                    </div>

                    <div className="supplier-details mb-3">
                      {supplier.contact_name && (
                        <div className="detail-row">
                          <i className="bi bi-person me-2 text-muted"></i>
                          <span>{supplier.contact_name}</span>
                        </div>
                      )}
                      {supplier.phone && (
                        <div className="detail-row">
                          <i className="bi bi-telephone me-2 text-muted"></i>
                          <a href={`tel:${supplier.phone}`} className="text-decoration-none">
                            {supplier.phone}
                          </a>
                        </div>
                      )}
                      {supplier.email && (
                        <div className="detail-row">
                          <i className="bi bi-envelope me-2 text-muted"></i>
                          <a href={`mailto:${supplier.email}`} className="text-decoration-none text-truncate">
                            {supplier.email}
                          </a>
                        </div>
                      )}
                      {supplier.payment_terms && (
                        <div className="detail-row">
                          <i className="bi bi-credit-card me-2 text-muted"></i>
                          <span>
                            {PAYMENT_TERMS_LABELS[supplier.payment_terms] || supplier.payment_terms}
                          </span>
                        </div>
                      )}
                    </div>

                    {canEdit && (
                      <div className="d-flex justify-content-end">
                        <KebabMenu label={`Acciones de ${supplier.name}`}>
                            <Dropdown.Item onClick={() => navigate(`/proveedores/${supplier.id}/editar`)}>
                              <i className="bi bi-pencil me-2"></i>
                              Editar
                            </Dropdown.Item>
                            <Dropdown.Item onClick={() => handleToggleStatus(supplier)}>
                              <i className={`bi ${supplier.is_active ? 'bi-pause-circle' : 'bi-play-circle'} me-2`}></i>
                              {supplier.is_active ? 'Desactivar' : 'Activar'}
                            </Dropdown.Item>
                            <Dropdown.Divider />
                            <Dropdown.Item
                              className="text-danger"
                              onClick={() => handleDeleteClick(supplier)}
                            >
                              <i className="bi bi-trash me-2"></i>
                              Eliminar
                            </Dropdown.Item>
                        </KebabMenu>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
          )
        ) : (
          <div className="p-5 text-center text-muted">
            <i className="bi bi-building fs-1 d-block mb-3"></i>
            <p className="mb-0">
              {searchTerm || statusFilter !== 'all'
                ? 'No se encontraron proveedores con los filtros aplicados'
                : 'No hay proveedores registrados'}
            </p>
            {canEdit && !searchTerm && statusFilter === 'all' && (
              <Button
                variant="primary"
                className="mt-3"
                onClick={() => navigate('/proveedores/nuevo')}
              >
                <i className="bi bi-plus-circle me-2"></i>
                Agregar Proveedor
              </Button>
            )}
          </div>
        )}

        {/* Delete Confirmation Modal */}
        <Modal show={deleteModalShow} onHide={() => setDeleteModalShow(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Confirmar Eliminacion</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {supplierToDelete && (
              <>
                <p>
                  Esta seguro de eliminar el proveedor{' '}
                  <strong>{supplierToDelete.name}</strong>?
                </p>
                <Alert variant="warning" className="mb-0">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  Si el proveedor tiene compras asociadas, sera desactivado en
                  lugar de eliminado. <strong>Si no las tiene, el borrado es
                  permanente</strong> y no se puede deshacer — si solo querés
                  dejar de usarlo, usá "Desactivar".
                </Alert>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setDeleteModalShow(false)}
              disabled={deleteMutation.isPending}
            >
              Cancelar
            </Button>
            <Button
              variant="danger"
              onClick={handleConfirmDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Eliminando...
                </>
              ) : (
                <>
                  <i className="bi bi-trash me-2"></i>
                  Eliminar
                </>
              )}
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </DashboardLayout>
  );
};

export default SupplierList;
